import type { ReactNode } from 'react';

import './phone-shell.css';

/**
 * PhoneShell — shared realistic-iPhone chrome for landing surfaces.
 *
 * Pure presentational, server-renderable (no 'use client', no interactivity,
 * no animation). Transcribed from the approved mockup's `.iphone*` / `.sb` /
 * `.island` / `.home-bar` markup — docs/design/mockups/homepage-v5-mockup.html
 * — with a `ps-` class prefix. Levitation and 3D hover-tilt are intentionally
 * NOT included here; owner components (hero stage, mini mounts) opt into
 * those animations themselves on top of this shell.
 *
 * `islandContent` fills the Dynamic Island slot (e.g. "Bri · ready" caller
 * status, or a keypad/forwarding label) and `children` fill the screen body
 * between the island and the home indicator.
 */

export interface PhoneShellProps {
  /** `hero` = full-size stage phone. `mini` = smaller secondary mount. */
  size: 'hero' | 'mini';
  /** Content rendered inside the Dynamic Island pill. */
  islandContent?: ReactNode;
  /** Additional class names merged onto the outer frame. */
  className?: string;
  /** Screen content, rendered between the Dynamic Island and home indicator. */
  children?: ReactNode;
}

export function PhoneShell({ size, islandContent, className, children }: PhoneShellProps) {
  const sizeClass = size === 'mini' ? 'ps-iphone--mini' : 'ps-iphone--hero';
  const frameClassName = ['ps-iphone', sizeClass, className].filter(Boolean).join(' ');

  return (
    <div className={frameClassName}>
      <i className="ps-ant ps-ant-t" aria-hidden="true" />
      <i className="ps-ant ps-ant-b" aria-hidden="true" />
      <i className="ps-btn ps-btn-action" aria-hidden="true" />
      <i className="ps-btn ps-btn-volup" aria-hidden="true" />
      <i className="ps-btn ps-btn-voldn" aria-hidden="true" />
      <i className="ps-btn ps-btn-power" aria-hidden="true" />
      <div className="ps-bezel">
        <div className="ps-screen">
          <div className="ps-sb" aria-hidden="true">
            <span>9:41</span>
            <span className="ps-sb-icons">
              <svg width="18" height="11" viewBox="0 0 18 11" fill="currentColor">
                <rect x="0" y="7" width="3" height="4" rx="0.8" />
                <rect x="4.5" y="5" width="3" height="6" rx="0.8" />
                <rect x="9" y="2.5" width="3" height="8.5" rx="0.8" />
                <rect x="13.5" y="0" width="3" height="11" rx="0.8" />
              </svg>
              <svg width="16" height="11" viewBox="0 0 16 11" fill="currentColor">
                <path
                  d="M8 9.2a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8zM8 5.4c-1.5 0-2.9.6-3.9 1.6l1.3 1.3a3.7 3.7 0 0 1 5.2 0l1.3-1.3A5.5 5.5 0 0 0 8 5.4zM8 1.5C5.4 1.5 3 2.6 1.2 4.3l1.3 1.3A7.7 7.7 0 0 1 8 3.4c2.1 0 4 .8 5.5 2.2l1.3-1.3A9.5 9.5 0 0 0 8 1.5z"
                  transform="translate(0 -1.5)"
                />
              </svg>
              <svg width="25" height="12" viewBox="0 0 25 12">
                <rect x="0.5" y="0.5" width="21" height="11" rx="3.2" fill="none" stroke="currentColor" opacity="0.4" />
                <rect x="2" y="2" width="15" height="8" rx="1.8" fill="currentColor" />
                <path d="M23 4v4a2.2 2.2 0 0 0 0-4z" fill="currentColor" opacity="0.4" />
              </svg>
            </span>
          </div>
          <span className="ps-island">{islandContent}</span>
          {children}
          <div className="ps-home-bar" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

export default PhoneShell;
